<template>
  <div>
      <b-row>
        <b-col cols="4" class="mb-3">
          <b-overlay :show="previewLoading" rounded="sm">
            <b-row v-if="attachments.length > 0">
              <b-col cols="12">
                <b-row>
                  <b-col cols="12">
                    <table class="min-w-full leading-normal mt-1 text-gray" style="border: solid 1px #dedede;">
                      <thead>
                      <tr class="bg-gray-pale">
                        <td colspan="12" class="px-2 py-2 font-bold">Attachments</td>
                      </tr>
                      <tr>
                        <th class="px-2 py-1">#</th>
                        <th class="px-2 py-1">Description</th>
                        <th class="px-2 py-1 float-end">Action</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(file, idx) in attachments" :key="file.id || file.identifier || idx" style="border: solid 1px #dedede;">
                        <td class="px-2 py-1">{{idx + 1}}</td>
                        <td class="px-2 py-1">{{ fileLabel(file) }}</td>
                        <td class="px-2 py-1">
                          <button
                            v-if="canPreview(file)"
                            class="float-end bg-gray rounded text-white hover:bg-gray-dark px-2"
                            @click="openFile(file)"
                          >
                            Show
                          </button>
                          <span v-else class="float-end text-gray text-xs">No preview</span>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
            <b-row v-else class="mt-2">
              <b-col cols="12" class="text-gray text-sm">
                No attachments uploaded for this application.
              </b-col>
            </b-row>
          </b-overlay>
        </b-col>
        <b-col cols="8">
          <div class="overflow-hidden bg-gray p-2">
            <iframe
              v-if="previewBlobUrl"
              class="pdf-preview"
              :src="previewBlobUrl + '#toolbar=0'"
              title="Attachment preview"
            ></iframe>
            <p v-else class="bg-gray min-h-screen text-center">
              <span class="py-5 text-white">Select a document to preview</span>
            </p>
          </div>
        </b-col>
      </b-row>
  </div>
</template>

<script>
import {api} from "@/app/services/api.service";

export default {
  name: "BusinessAnnualReturnFormFiles",
  props:{
    trackingNumber:String,
    task:Object,
  },
  data(){
    return{
      previewLoading:false,
      previewBlobUrl:null,
    }
  },
  mounted(){
    this.previewFirstAttachment()
  },
  beforeDestroy(){
    this.revokePreviewUrl()
  },
  methods:{
    fileLabel(file){
      if(!file || typeof file !== "object") return "Attachment"
      return file.description || file.purpose || file.identifier || file.fileName || "Attachment"
    },
    attachmentPreviewPath(file){
      if(!file || typeof file !== "object"){
        return ""
      }

      const path = file.path
      if(typeof path !== "string" || !path.trim()){
        return ""
      }

      const normalizedPath = path.trim()
      if(normalizedPath.startsWith("/business-registration-v2/")){
        return normalizedPath
      }

      return `/business-registration-v2${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`
    },
    canPreview(file){
      return this.attachmentPreviewPath(file) !== ""
    },
    mergeAttachments(list){
      const mergedByIdentifier = new Map()

      list.forEach((item) => {
        if(!item || typeof item !== "object"){
          return
        }

        const key = item.identifier || item.id || item.description || item.fileName || item.path
        if(!key){
          return
        }

        const existing = mergedByIdentifier.get(key)
        if(!existing){
          mergedByIdentifier.set(key, {...item})
          return
        }

        mergedByIdentifier.set(key, {
          ...existing,
          ...item,
          id: item.id || existing.id,
          description: item.description || existing.description,
          identifier: item.identifier || existing.identifier,
          fileName: item.fileName || existing.fileName,
          path: item.path || existing.path,
        })
      })

      return Array.from(mergedByIdentifier.values())
    },
    revokePreviewUrl(){
      if(this.previewBlobUrl){
        URL.revokeObjectURL(this.previewBlobUrl)
        this.previewBlobUrl = null
      }
    },
    previewFirstAttachment(){
      const list = this.attachments
      const previewable = list.find((file) => this.canPreview(file))
      if(previewable){
        this.openFile(previewable)
      }
    },
    isPdfBytes(bytes){
      return bytes.length >= 4
        && bytes[0] === 0x25
        && bytes[1] === 0x50
        && bytes[2] === 0x44
        && bytes[3] === 0x46
    },
    parseHexDump(text){
      const pairs = String(text || "").trim().split(/\s+/).filter(Boolean)
      if(pairs.length === 0 || !pairs.every((pair) => /^[0-9A-Fa-f]{2}$/.test(pair))){
        return null
      }
      const bytes = new Uint8Array(pairs.length)
      for(let index = 0; index < pairs.length; index += 1){
        bytes[index] = parseInt(pairs[index], 16)
      }
      return bytes
    },
    async readAttachmentBytes(blob){
      const buffer = blob && typeof blob.arrayBuffer === "function"
        ? await blob.arrayBuffer()
        : await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = () => reject(reader.error || new Error("Failed to read attachment"))
          reader.readAsArrayBuffer(blob)
        })
      const bytes = new Uint8Array(buffer)
      if(this.isPdfBytes(bytes)){
        return bytes
      }
      const hexBytes = this.parseHexDump(new TextDecoder("utf-8").decode(bytes))
      return hexBytes || bytes
    },
    async openFile(data){
      const previewPath = this.attachmentPreviewPath(data)
      if(!previewPath){
        this.errorToast("Document Error", "Preview is not available for this attachment")
        return
      }

      this.previewLoading = true
      this.revokePreviewUrl()
      try {
        const response = await api().get(previewPath, {
          responseType: "blob",
          headers: {
            Accept: "application/pdf",
          },
        })
        const bytes = await this.readAttachmentBytes(response.data)
        const previewBlob = new Blob([bytes], {type: "application/pdf"})
        this.previewBlobUrl = URL.createObjectURL(previewBlob)
      } catch (error) {
        const message = error?.response?.data?.message || "Failed to load attachment preview"
        this.errorToast("Error", message)
      } finally {
        this.previewLoading = false
      }
    },
  },
  computed:{
    attachments(){
      let list = []
      if(this.task && Array.isArray(this.task.attachments)){
        list = this.task.attachments
      } else if(this.task && this.task.data && Array.isArray(this.task.data.attachments)){
        list = this.task.data.attachments
      }

      return this.mergeAttachments(list)
    },
  },
  watch:{
    task:{
      deep: true,
      handler(){
        this.previewFirstAttachment()
      },
    },
    attachments(list){
      if(list && list.length > 0){
        this.previewFirstAttachment()
      } else {
        this.revokePreviewUrl()
      }
    },
  },
}
</script>

<style scoped>
th, td{
  font-size: 12px;
}

.pdf-preview{
  width: 100%;
  height: 800px;
  overflow-x: auto;
  border: 1px solid #b1b1b1;
}
</style>
